import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Theme = "light" | "dark" | "system"
 *
 * The CSS variables for both palettes already exist (see index.css :root
 * + .dark). This provider only flips the `.dark` class on <html> based on
 * the user's choice. "system" subscribes to prefers-color-scheme so the
 * app follows iOS Appearance.
 *
 * Persistence: localStorage key `kinly.theme`. We hydrate synchronously
 * (no flash of wrong theme) by reading localStorage in the initializer.
 */
const KEY = "kinly.theme";

function _read() {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch { /* noop */ }
  return "system";
}

function _systemDark() {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

function _apply(theme) {
  const root = document.documentElement;
  const dark = theme === "dark" || (theme === "system" && _systemDark());
  root.classList.toggle("dark", dark);
  root.dataset.theme = dark ? "dark" : "light";
  // Native status-bar coordination — Capacitor reads this on resume.
  root.style.colorScheme = dark ? "dark" : "light";
  // Sync iOS status-bar icon colour + chrome background. Lazy-imported
  // so the bundle doesn't pay the cost on web preview. Errors are
  // swallowed inside the helper — never block the theme flip.
  import("@/lib/statusBar")
    .then((m) => m.syncNativeChromeToTheme?.())
    .catch(() => {});
}

const ThemeContext = createContext({
  theme: "system",
  resolved: "light",
  setTheme: () => {},
});

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(_read);
  const [resolved, setResolved] = useState(() => (
    (_read() === "dark" || (_read() === "system" && _systemDark())) ? "dark" : "light"
  ));

  // Apply on every theme change.
  useEffect(() => {
    _apply(theme);
    setResolved(theme === "dark" || (theme === "system" && _systemDark()) ? "dark" : "light");
    try { localStorage.setItem(KEY, theme); } catch { /* noop */ }
  }, [theme]);

  // Watch system pref when in "system" mode so the app re-flips when iOS
  // toggles dark mode at sunset.
  useEffect(() => {
    if (theme !== "system") return;
    let mql;
    try { mql = window.matchMedia("(prefers-color-scheme: dark)"); } catch { return; }
    const onChange = () => {
      _apply("system");
      setResolved(_systemDark() ? "dark" : "light");
    };
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, [theme]);

  const setTheme = useCallback((t) => setThemeState(t), []);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

// Pre-mount hook for App.jsx — apply the persisted theme BEFORE React
// hydrates so users never see a one-frame light flash. Call this in
// index.js or App.jsx at module top.
export function applyPersistedThemeEarly() {
  try { _apply(_read()); } catch { /* noop */ }
}
